/**
 * ReportSuspicion.jsx: BM-H3819 (6 Oct 2026). Report a suspicion to the MLRO.
 *
 * AML Policy (POL-01 v5): "Any concerns, suspicions or queries about a client
 * or matter must be notified to the MLRO immediately using the appropriate
 * Notification Form." Any member of staff can report. The report goes to the
 * MLRO as an MLRO alert and nowhere else; it is not filed to the matter.
 * Server: POST /api/compliance/mlro-alerts/suspicion-report.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldAlert } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const id = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return id ? { "X-Bonsai-User-Id": id } : {};
}

const INPUT = "w-full px-2 py-1.5 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-100";

export default function ReportSuspicion() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ matter_reference: params.get("matter") || "", concern: "", noticed_on: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/compliance/mlro-alerts/suspicion-report`, form, { headers: authHeaders() });
      setDone(r.data);
    } catch (err) {
      setError(errorMessage(err, "The report could not be sent to the MLRO"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="p-6 max-w-2xl space-y-3" data-testid="suspicion-reported">
        <h1 className="text-xl font-semibold text-slate-100">Reported to the MLRO</h1>
        <p className="text-sm text-slate-200">{done.message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-6 max-w-2xl space-y-4" data-testid="suspicion-form">
      <div className="flex items-center gap-2">
        <ShieldAlert size={20} className="text-amber-300" />
        <h1 className="text-xl font-semibold text-slate-100">Report a suspicion to the MLRO</h1>
      </div>
      <p className="text-sm text-slate-300">
        Use this for any concern, suspicion or query about money laundering, terrorist financing, sanctions,
        fraud or tax evasion on a client or matter (AML Policy). It goes only to the MLRO. Report it now,
        even if you are unsure: deciding whether it is suspicious is the MLRO's job.
      </p>
      <label className="block space-y-1">
        <span className="text-sm text-slate-200">Matter reference (if there is one)</span>
        <input id="suspicion-matter" className={INPUT} value={form.matter_reference} onChange={set("matter_reference")} />
      </label>
      <label className="block space-y-1">
        <span className="text-sm text-slate-200">What made you suspicious?</span>
        <textarea id="suspicion-concern" required rows={6} className={INPUT} value={form.concern} onChange={set("concern")}
          placeholder="What happened, who was involved, and anything you were asked to do." />
      </label>
      <label className="block space-y-1">
        <span className="text-sm text-slate-200">When did you notice it?</span>
        <input id="suspicion-noticed" type="date" className={INPUT} value={form.noticed_on} onChange={set("noticed_on")} />
      </label>
      <p className="text-xs text-amber-200">
        Do not tell the client, or anyone outside the firm, that you are making this report. Tipping off is a
        criminal offence.
      </p>
      {error && <div className="text-sm text-red-300">{error}</div>}
      <button type="submit" disabled={busy} data-testid="suspicion-submit"
        className="px-4 py-2 text-sm rounded-md bg-amber-700 text-white disabled:opacity-50">
        {busy ? "Sending…" : "Send to the MLRO"}
      </button>
    </form>
  );
}
